import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import RoomIcon from '@mui/icons-material/Room';
import Paper from '@mui/material/Paper';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import HotelEditRoomsRow from 'components/HotelEditRoomsRow';

import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';
import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';

import Query, {
  HotelEditRoomsRefetchQuery,
} from '__generated__/HotelEditRoomsRefetchQuery.graphql';
import { HotelEditRooms_hotel$key } from '__generated__/HotelEditRooms_hotel.graphql';
import { useTableSort } from 'components/TableSorting';
import FabDialog from 'components/FabDialog';
import AddRoomDialog from 'components/AddRoomDialog';

type Props = {
  hotel: HotelEditRooms_hotel$key;
};

const HotelEditRooms: FC<Props> = props => {
  const { variables } = usePagination();
  const { search } = useSearch();
  const { sort } = useTableSort();

  const [data, { refetch, loading }] = useAvoidSuspense<
    HotelEditRoomsRefetchQuery,
    HotelEditRooms_hotel$key
  >(
    graphql`
      fragment HotelEditRooms_hotel on Hotel
      @refetchable(queryName: "HotelEditRoomsRefetchQuery") {
        id
        rooms(first: $first, after: $after, search: $search, sort: $sort) {
          __id
          edges {
            node {
              id

              ...HotelEditRoomsRow_room
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }

        ...AddRoomDialog_hotel
      }
    `,
    props.hotel,
    Query
  );

  const connectionId = data.rooms?.__id;

  const [deleteRoom, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = data.rooms ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        id: data.id,
        ...variables,
        search,
        sort,
      });
    }, [data.id, refetch, search, sort, variables])
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<RoomIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>

            <TableHeadCell center name="capacity">
              Vietų skaičius
            </TableHeadCell>

            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <HotelEditRoomsRow key={node.id} room={node} onDelete={deleteRoom} />
        ))}
      </Table>

      <FabDialog>
        <AddRoomDialog hotel={data} />
      </FabDialog>
    </Paper>
  );
};

export default HotelEditRooms;
